/**
 * Boten Lijst Pagina
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { botenService, BootDetail } from '../../services/boten.service';
import ConfirmDialog from '../../components/ConfirmDialog';
import ErrorDialog from '../../components/ErrorDialog';
import { useAuth } from '../../context/AuthContext';
import './Boten.css';

const BotenList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [boten, setBoten] = useState<BootDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoek, setZoek] = useState('');
  const [debouncedZoek, setDebouncedZoek] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; naam: string } | null>(null);
  const [sortField, setSortField] = useState<'bootnaam' | 'eigenaar'>('bootnaam');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const tableHeaderRef = useRef<HTMLDivElement>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [tableLeft, setTableLeft] = useState(0);
  const [tableWidth, setTableWidth] = useState(0);

  // Handle search input
  const handleSearch = (value: string) => {
    setZoek(value);
  };

  // Debounce zoekterm
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedZoek(zoek);
    }, 300);
    return () => clearTimeout(timer);
  }, [zoek]);

  // Laad boten
  const loadBoten = useCallback(async () => {
    try {
      setLoading(true);
      const data = await botenService.getAll({
        zoek: debouncedZoek || undefined,
        limit: 50,
        sorteer: sortField,
        richting: sortDirection,
        actief: true
      });
      setBoten(data);
      setHasMore(data.length === 50);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden bij het ophalen van de boten');
    } finally {
      setLoading(false);
    }
  }, [debouncedZoek, sortField, sortDirection]);

  // Laad meer boten
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const data = await botenService.getAll({
        zoek: debouncedZoek || undefined,
        limit: 50,
        skip: boten.length,
        actief: true
      });
      setBoten(prev => [...prev, ...data]);
      setHasMore(data.length === 50);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, boten.length, debouncedZoek]);

  // Laad boten wanneer zoekterm of sorting verandert
  useEffect(() => {
    loadBoten();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedZoek, sortField, sortDirection]);

  // Handle sort
  const handleSort = (field: 'bootnaam' | 'eigenaar') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollHeight - scrollTop - clientHeight < 300 && hasMore && !loadingMore) {
        handleLoadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, loadingMore, handleLoadMore]);

  // Sticky header handler
  useEffect(() => {
    if (!boten.length) return;

    const handleScroll = () => {
      if (tableHeaderRef.current) {
        const rect = tableHeaderRef.current.getBoundingClientRect();
        const shouldBeSticky = rect.bottom <= 185;
        setTableLeft(rect.left);
        setTableWidth(rect.width);
        setIsHeaderSticky(shouldBeSticky);
      }
    };

    const timeout = setTimeout(() => {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      handleScroll();
    }, 100);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [boten]);

  // Helper: Bepaal display naam voor eigenaar
  const getEigenaarDisplayNaam = (boot: BootDetail) => {
    if (!boot.eigenaar) return '-';

    if (boot.eigenaar.bedrijfsnaam === 'Particulier' && boot.eigenaar.achternaam) {
      const firstName = [
        boot.eigenaar.voornaam,
        boot.eigenaar.tussenvoegsel
      ].filter(Boolean).join(' ');
      return firstName ? `${boot.eigenaar.achternaam}, ${firstName}` : boot.eigenaar.achternaam;
    }
    return boot.eigenaar.bedrijfsnaam;
  };

  const handleDeleteClick = (id: number, naam: string) => {
    setConfirmDelete({ id, naam });
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;

    try {
      await botenService.delete(confirmDelete.id);
      setConfirmDelete(null);
      loadBoten();
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden bij het verwijderen');
      setConfirmDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDelete(null);
  };

  if (loading && boten.length === 0) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="boten-list">
      <div className="page-header">
        <h1>Boten</h1>
        {hasPermission('boten_aanmaken') && (
          <button
            className="btn-primary"
            onClick={() => navigate('/boten/new')}
          >
            + Nieuwe Boot
          </button>
        )}
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Zoek op bootnaam of registratienummer..."
          value={zoek}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      {/* Vaste header rij (altijd zichtbaar) */}
      {boten.length > 0 && (
        <div ref={tableHeaderRef} className="table-header" style={{
          display: 'flex',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #dee2e6',
          padding: '0.75rem 1rem',
          marginTop: '1rem',
          borderRadius: '8px 8px 0 0',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#495057'
        }}>
          <div onClick={() => handleSort('bootnaam')} style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem', cursor: 'pointer' }}>
            Bootnaam {sortField === 'bootnaam' && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
          <div onClick={() => handleSort('eigenaar')} style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem', cursor: 'pointer' }}>
            Eigenaar {sortField === 'eigenaar' && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Model</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>MMSI</div>
          <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Ligplaats</div>
          <div style={{ flex: '2', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {/* Sticky header overlay (duplicaat) */}
      {isHeaderSticky && (
        <div className="table-header" style={{
          position: 'fixed',
          top: '185px',
          left: `${tableLeft}px`,
          width: `${tableWidth}px`,
          zIndex: 1000,
          display: 'flex',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #dee2e6',
          borderRadius: '8px 8px 0 0',
          padding: '0.75rem 1rem',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#495057'
        }}>
          <div onClick={() => handleSort('bootnaam')} style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem', cursor: 'pointer' }}>
            Bootnaam {sortField === 'bootnaam' && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
          <div onClick={() => handleSort('eigenaar')} style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem', cursor: 'pointer' }}>
            Eigenaar {sortField === 'eigenaar' && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Model</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>MMSI</div>
          <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Ligplaats</div>
          <div style={{ flex: '2', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {/* Boten lijst - Desktop */}
      {boten.length > 0 && (
        <div className="desktop-table" style={{
          background: 'white',
          borderRadius: '0 0 8px 8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          {boten.map((boot) => (
            <div key={boot.id} style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.75rem 1rem',
              borderBottom: '1px solid #dee2e6',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <div style={{ flex: '1.5', minWidth: 0, fontWeight: 500, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{boot.bootnaam || '-'}</div>
              <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getEigenaarDisplayNaam(boot)}</div>
              <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{`${boot.boot_model.fabrikant} ${boot.boot_model.model}`}</div>
              <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{boot.mmsi || '-'}</div>
              <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{boot.ligplaats || '-'}</div>
              <div className="actions" style={{ flex: '2', minWidth: 0 }}>
                <button
                  className="btn-small btn-secondary"
                  onClick={() => navigate(`/boten/${boot.id}`)}
                  title="Bekijk boot details"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Bekijken
                </button>
                {boot.eigenaar_id && hasPermission('relaties_bekijken') && (
                  <button
                    className="btn-small btn-primary"
                    onClick={() => navigate(`/relations/${boot.eigenaar_id}`)}
                    title="Bekijk eigenaar"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Eigenaar
                  </button>
                )}
                {hasPermission('boten_verwijderen') && (
                  <button
                    className="btn-small btn-danger"
                    onClick={() => handleDeleteClick(boot.id, boot.bootnaam || boot.registratienummer || 'deze boot')}
                    title="Verwijder boot"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Verwijderen
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Boten lijst - Mobile tegels */}
      {boten.length > 0 && (
        <div className="mobile-cards">
          {boten.map((boot) => (
            <div key={boot.id} className="boot-card" onClick={() => navigate(`/boten/${boot.id}`)}>
              <div className="boot-card-header">
                <span className="boot-name">{boot.bootnaam || boot.registratienummer || '-'}</span>
              </div>
              <div className="boot-card-body">
                <div className="boot-info"><span className="label">Eigenaar:</span> {getEigenaarDisplayNaam(boot)}</div>
                <div className="boot-info"><span className="label">Model:</span> {`${boot.boot_model.fabrikant} ${boot.boot_model.model}`}</div>
                {boot.ligplaats && (
                  <div className="boot-info"><span className="label">Ligplaats:</span> {boot.ligplaats}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {boten.length === 0 && !loading && (
        <div style={{
          padding: '3rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          textAlign: 'center',
          marginTop: '1rem'
        }}>
          Geen boten gevonden
        </div>
      )}

      {loadingMore && (
        <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
          Meer laden...
        </div>
      )}

      {!hasMore && boten.length > 0 && (
        <div style={{ textAlign: 'center', padding: '1rem', color: '#999' }}>
          Alle boten geladen ({boten.length} {boten.length === 1 ? 'boot' : 'boten'})
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Boot Verwijderen"
        message={`Weet je zeker dat je boot "${confirmDelete?.naam}" wilt verwijderen?`}
        confirmText="Verwijderen"
        cancelText="Annuleren"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        danger={true}
      />

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default BotenList;
